/**
 * Карточка позиции — Phase 4.
 *
 * Открывается из строки таблицы Номенклатуры (клик по артикулу/имени).
 * Показывает: остатки по складам, on_order + reserved (SO), помесячный
 * оборот, движения за 90 дней, топ-поставщиков и топ-покупателей.
 */
import { useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

import { Spinner } from "@/components/Spinner";
import { fetchItemCard, formatRub, monthLabel } from "@/api/warehouse";
import { ApiError } from "@/api/client";


export function WarehouseItemCardPage() {
  const params = useParams({ strict: false }) as { itemId?: string };
  const itemId = params.itemId ? parseInt(params.itemId, 10) : NaN;

  const q = useQuery({
    queryKey: ["warehouse-item-card", itemId],
    queryFn: () => fetchItemCard(itemId, 90),
    enabled: !isNaN(itemId),
  });

  if (isNaN(itemId)) return <div className="alert alert-red"><div className="alert-body">Неверный URL</div></div>;
  if (q.isLoading) return <Spinner size="lg" label="Загрузка карточки…" />;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    if (err instanceof ApiError && err.status === 404) return <div className="alert alert-red"><div className="alert-body">Позиция не найдена</div></div>;
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }
  const data = q.data!;
  const { item, stocks, movements, by_month, top_suppliers, top_customers } = data;

  const totalQty = stocks.reduce((s, x) => s + x.quantity, 0);
  const totalReserved = stocks.reduce((s, x) => s + x.reserved, 0);
  const available = totalQty - Math.max(totalReserved, data.reserved_so);

  // Соковые предупреждения
  const today = new Date().toISOString().slice(0, 10);
  const expiresSoon = item.expires_at && item.expires_at < new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const expired = item.expires_at && item.expires_at < today;

  const months12 = by_month.map(m => ({
    month: monthLabel(m.month),
    Приход: m.inflow,
    Отгрузка: m.outflow,
  }));

  return (
    <div className="page">

      {/* Шапка */}
      <div className="card" style={{ padding: 16, marginBottom: "var(--gap)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700 }}>
              {item.sku && <span className="tabular" style={{ color: "var(--mute)", marginRight: 8 }}>{item.sku}</span>}
              {item.name}
            </div>
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4 }}>
              {item.section && <>{item.section} · </>}
              {item.unit && <>Ед.: {item.unit} · </>}
              Создан: {item.created_at?.slice(0, 10)}
            </div>
            {item.note && <div style={{ fontSize: 12, marginTop: 6 }}>{item.note}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            {item.price_buy != null && <div>Закуп: <strong>{formatRub(item.price_buy)}</strong></div>}
            {item.price_sell != null && <div style={{ fontSize: 13, color: "var(--mute)" }}>Розн.: {formatRub(item.price_sell)}</div>}
            {item.expires_at && (
              <div style={{ fontSize: 12, marginTop: 6,
                            color: expired ? "var(--brick)" : expiresSoon ? "#e99a12" : "var(--mute)" }}>
                {expired ? "Просрочена: " : expiresSoon ? "Истекает: " : "Срок годности: "}
                <strong>{item.expires_at}</strong>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI: остатки + доступно + on_order + reserved */}
      <div className="row row-4" style={{ marginBottom: "var(--gap)" }}>
        <KPI label="На складе" value={fmt(totalQty, item.unit)} foot={`по ${stocks.length} складам`} />
        <KPI label="Резерв" value={fmt(Math.max(totalReserved, data.reserved_so), item.unit)}
             foot={data.reserved_so > 0 ? `авто из SO: ${fmt(data.reserved_so, item.unit)}` : "ручной"} />
        <KPI label="В пути" value={fmt(data.on_order, item.unit)} foot="по открытым PO" />
        <KPI label="Доступно" value={fmt(available, item.unit)} alarm={available < 0} />
      </div>

      {/* Остатки по складам */}
      <div className="card" style={{ padding: 12, marginBottom: "var(--gap)" }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Остатки по складам</div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Склад</th>
              <th className="num">Кол-во</th>
              <th className="num">Бронь</th>
              <th className="num">Критич.</th>
              <th>Обновлено</th>
            </tr>
          </thead>
          <tbody>
            {stocks.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: 20, color: "var(--mute)" }}>Нет на складах</td></tr>
            ) : stocks.map(s => (
              <tr key={s.warehouse}>
                <td>{s.warehouse}</td>
                <td className="num tabular strong">{s.quantity} {item.unit || ""}</td>
                <td className="num tabular">{s.reserved}</td>
                <td className="num tabular" style={{ color: s.critical_qty != null && s.quantity <= s.critical_qty ? "var(--brick)" : "var(--mute)" }}>
                  {s.critical_qty ?? "—"}
                </td>
                <td style={{ fontSize: 12, color: "var(--mute)" }}>{s.updated_at?.slice(0, 16).replace("T", " ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* График по месяцам */}
      {months12.some(m => m.Приход > 0 || m.Отгрузка > 0) && (
        <div className="card" style={{ padding: 12, marginBottom: "var(--gap)" }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Оборот по месяцам (12 мес)</div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={months12}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Приход" fill="#3D7A3D" />
                <Bar dataKey="Отгрузка" fill="#AB2B2B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Топы */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--gap)", marginBottom: "var(--gap)" }}>
        {top_suppliers.length > 0 && (
          <div className="card" style={{ padding: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Топ-поставщики</div>
            <table className="tbl">
              <thead><tr><th>Поставщик</th><th className="num">Док.</th><th className="num">Кол-во</th><th className="num">Сумма</th></tr></thead>
              <tbody>
                {top_suppliers.map((s, i) => (
                  <tr key={i}>
                    <td>{s.name}</td>
                    <td className="num tabular">{s.docs}</td>
                    <td className="num tabular">{s.qty}</td>
                    <td className="num tabular">{formatRub(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {top_customers.length > 0 && (
          <div className="card" style={{ padding: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Топ-покупатели</div>
            <table className="tbl">
              <thead><tr><th>Покупатель</th><th className="num">Док.</th><th className="num">Кол-во</th><th className="num">Сумма</th></tr></thead>
              <tbody>
                {top_customers.map((s, i) => (
                  <tr key={i}>
                    <td>{s.name}</td>
                    <td className="num tabular">{s.docs}</td>
                    <td className="num tabular">{s.qty}</td>
                    <td className="num tabular">{formatRub(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Движения */}
      <div className="card" style={{ padding: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Движения за 90 дней ({movements.length})</div>
        {movements.length === 0 ? (
          <div className="muted" style={{ textAlign: "center", padding: 30 }}>Движений за период не было</div>
        ) : (
          <div className="tbl-scroll" style={{ maxHeight: "50vh" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th style={{ width: 90 }}>Тип</th>
                  <th className="num">Кол-во</th>
                  <th>Контрагент</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m, i) => (
                  <tr key={i}>
                    <td className="tabular" style={{ fontSize: 12 }}>{m.date}</td>
                    <td>
                      <span style={{ display: "inline-block", padding: "1px 8px", fontSize: 11, borderRadius: 10,
                                     background: "var(--paper)",
                                     color: m.direction === "in" ? "#3D7A3D" : "var(--brick)",
                                     fontWeight: 600 }}>
                        {m.direction === "in" ? "Приход" : "Расход"}
                      </span>
                    </td>
                    <td className="num tabular strong">
                      <span style={{ color: m.direction === "in" ? "#3D7A3D" : "var(--brick)" }}>
                        {m.direction === "in" ? "+" : "−"}{m.quantity} {item.unit || ""}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: "var(--mute)" }}>{m.counterparty || "—"}</td>
                    <td className="num tabular">{formatRub(m.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function KPI({ label, value, foot, alarm }: { label: string; value: string; foot?: string; alarm?: boolean }) {
  return (
    <div className={`kpi ${alarm ? "alarm" : ""}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value kpi-value-sm">{value}</div>
      {foot && <div className="kpi-foot"><span style={{ fontSize: 11, color: "var(--mute)" }}>{foot}</span></div>}
    </div>
  );
}

function fmt(v: number, unit: string | null): string {
  return `${Number(v.toFixed(3))} ${unit || ""}`.trim();
}
